import { useQuery } from '@tanstack/react-query';
import { type LiveResource, toLiveResource } from '@/data/liveResource';
import { MINUTE, SECOND } from '../types';
import { config } from './config';

export type LifeEventType = 'focus' | 'vitals' | 'location' | 'sleep';

export interface LifeEvent {
  eventTime: Date;
  receivedAt: Date;
  type: LifeEventType;
  source: string | null;
  focus: { name: string | null; isActive: boolean | null } | null;
  vitals: {
    heartRate: number | null;
    restingHeartRate: number | null;
    steps: number | null;
    activeEnergy: number | null;
    oxygenSaturation: number | null;
  } | null;
  location: {
    city: string | null;
    country: string | null;
    latitude: number | null;
    longitude: number | null;
  } | null;
  sleep: {
    totalDurationMinutes: number | null;
    bedtime: Date | null;
    wakeTime: Date | null;
  } | null;
}

export interface HeartRatePoint {
  time: Date;
  value: number;
}

export interface LifeData {
  events: LifeEvent[];
  heartRate: HeartRatePoint[];
}

type FetchedEvent = Omit<LifeEvent, 'eventTime' | 'receivedAt' | 'sleep'> & {
  eventTime: string;
  receivedAt: string;
  sleep:
    | (Omit<NonNullable<LifeEvent['sleep']>, 'bedtime' | 'wakeTime'> & {
        bedtime: string | null;
        wakeTime: string | null;
      })
    | null;
};

export type LifeEventsWindow = 'day' | 'week' | 'month';

const REFETCH_MS: Record<LifeEventsWindow, number> = {
  day: 30 * SECOND,
  week: 5 * MINUTE,
  month: 60 * MINUTE,
};

const fetchLifeEvents = async (
  window: LifeEventsWindow
): Promise<LifeData> => {
  const res = await fetch(`${config.url}/${window}`);
  const data: {
    events: FetchedEvent[];
    heartRate?: { time: string; value: number }[];
  } = await res.json();

  const events = data.events.map((event) => ({
    ...event,
    eventTime: new Date(event.eventTime),
    receivedAt: new Date(event.receivedAt),
    sleep: event.sleep
      ? {
          ...event.sleep,
          bedtime: event.sleep.bedtime ? new Date(event.sleep.bedtime) : null,
          wakeTime: event.sleep.wakeTime ? new Date(event.sleep.wakeTime) : null,
        }
      : null,
  }));

  const heartRate = (data.heartRate ?? []).map((point) => ({
    time: new Date(point.time),
    value: point.value,
  }));

  return { events, heartRate };
};

export const useLifeEvents = (
  window: LifeEventsWindow = 'day'
): LiveResource<LifeData> => {
  const query = useQuery({
    queryKey: ['lifeEvents', window],
    queryFn: () => fetchLifeEvents(window),
    refetchInterval: REFETCH_MS[window],
  });
  return toLiveResource(query, {
    source: (data) => data.events.at(-1)?.source,
    freshAt: (data) => data.events.at(-1)?.receivedAt,
    staleAfterMs: config.staleAfterMiliseconds,
  });
};
